import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { MUIRadioCard } from 'components/common/MUICards'
import { carouselScrollState } from './carouselScroll'
import { CarouselScrollArrow } from './CarouselScrollArrow.component'
import type { FilteredCategory } from './useCatalogueFiltering'

interface Props {
  /** Entries to offer, in order: the catalogue's categories, led by Featured when packs are shown. */
  categories: Array<Pick<FilteredCategory, 'id' | 'displayName'>>
  /** The category on screen, which the row shows as chosen. */
  activeCategoryId: string | null
  /** Called when the client picks a category. */
  onSelectCategory: (categoryId: string) => void
  /** Whether to show the scroll arrows; on touch the row is swiped instead. */
  showArrows: boolean
  /** Inner padding of the scroller, so the first and last card can sit inside a page gutter while the row runs edge to edge. */
  inlinePadding?: string
}

/** Width of the fade over a cut-off card. */
const FADE_WIDTH = '7.2rem'

/** How far a scroll arrow moves the row. */
const ARROW_STEP_RATIO = 0.6

/**
 * The catalogue's category row: one radio card per category (design-system RadioButton card, `sm`, hug width),
 * on one line that scrolls sideways at every width rather than wrapping. A fade and an arrow appear on a side
 * only while there is more that way.
 *
 * Choosing a card asks the page to scroll to that category; the page's scroll-spy then reports it back as the
 * active one, and a card the spy activates off-screen is brought into view.
 */
export const CategoryRadioNav: React.FC<Props> = ({
  categories,
  activeCategoryId,
  onSelectCategory,
  showArrows,
  inlinePadding = '0.4rem',
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [{ canScrollPrev, canScrollNext }, setScrollState] = useState({ canScrollPrev: false, canScrollNext: false })

  const syncScroll = useCallback(() => {
    const el = scrollerRef.current
    if (el) setScrollState(carouselScrollState(el))
  }, [])

  useEffect(() => {
    syncScroll()
    window.addEventListener('resize', syncScroll)
    return () => window.removeEventListener('resize', syncScroll)
  }, [syncScroll, categories])

  useEffect(() => {
    const scroller = scrollerRef.current
    const active = scroller?.querySelector<HTMLElement>(`[data-category="${activeCategoryId}"]`)
    if (!scroller || !active) return
    const card = active.getBoundingClientRect()
    const row = scroller.getBoundingClientRect()
    if (card.left >= row.left && card.right <= row.right) return
    scroller.scrollTo({ left: scroller.scrollLeft + (card.left - row.left) - 16, behavior: 'smooth' })
  }, [activeCategoryId])

  const scrollBy = useCallback((direction: 'prev' | 'next') => {
    const el = scrollerRef.current
    if (!el) return
    const step = el.clientWidth * ARROW_STEP_RATIO
    el.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' })
  }, [])

  const fadeStart = canScrollPrev ? FADE_WIDTH : '0px'
  const fadeEnd = canScrollNext ? FADE_WIDTH : '0px'
  const mask = `linear-gradient(to right, transparent, #000 ${fadeStart}, #000 calc(100% - ${fadeEnd}), transparent)`

  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>

      <Stack
        ref={scrollerRef}
        onScroll={syncScroll}
        direction="row"
        gap={0.8}
        role="radiogroup"
        aria-label={t('purchase_flow_revamp:catalog.categories_aria')}
        data-cy="pf-revamp-category-nav"
        sx={{
          overflowX: 'auto',
          flexWrap: 'nowrap',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          // Room for the cards' 2px focus ring, which the scroller would otherwise clip.
          py: '0.4rem',
          px: inlinePadding,
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      >
        {categories.map((category) => (
          <Box key={category.id} data-category={category.id} sx={{ flexShrink: 0, display: 'flex' }}>
            <MUIRadioCard
              name="pf-category"
              value={category.id}
              label={category.displayName}
              checked={category.id === activeCategoryId}
              onChange={onSelectCategory}
              size="sm"
              width="hug"
              data-cy={`pf-revamp-category-${category.id}`}
            />
          </Box>
        ))}
      </Stack>

      {showArrows && canScrollPrev &&
        <CarouselScrollArrow direction="prev" onClick={scrollBy} label={t('purchase_flow_revamp:catalog.categories_scroll_left')} top="50%" inset="0" data-cy="pf-revamp-category-scroll-prev" />
      }

      {showArrows && canScrollNext &&
        <CarouselScrollArrow direction="next" onClick={scrollBy} label={t('purchase_flow_revamp:catalog.categories_scroll_right')} top="50%" inset="0" data-cy="pf-revamp-category-scroll-next" />
      }

    </Box>
  )
}
